import { v4 as uuidv4 } from 'uuid';
import type { Identity } from '../../auth/identityProvider';
import { getActiveTeamBundleKey } from '../../features/tenant/activeTenant';

/**
 * TS mirror of the Smithy MetaInfo structure and the builder used to construct
 * it for every operation.
 *
 * IMPORTANT: metaInfo is a BODY member of every request — it is serialized into
 * the JSON request body, NOT sent as a header.
 *
 * Pure TS — no React.
 */

/**
 * High-level business-domain classification of the job.
 * Mirrors the backend JobCategory enum.
 */
export type JobCategory =
  | 'DOCUMENT_MANAGEMENT'
  | 'ESTIMATION'
  | 'WORK_TRACKING'
  | 'TEMPLATE_MANAGEMENT'
  | 'PEOPLE_DIRECTORY'
  | 'KNOWLEDGE_DISCOVERY'
  | 'AI_ASSIST';

/**
 * Operation classification of the request. Mirrors the backend RequestType
 * enum — one value per modeled operation (People values retained for contract
 * fidelity even though the People capability is omitted from this client).
 */
export type RequestType =
  // Store
  | 'CREATE_META_INFO'
  | 'UPDATE_META_INFO'
  | 'FETCH_META_INFO'
  | 'LIST_META_INFO'
  | 'UPLOAD_META_DATA'
  | 'READ_META_DATA'
  // Inference
  | 'INVOKE_MODEL'
  | 'CONVERSE'
  // Knowledge
  | 'GET_PACKAGE_BY_NAME'
  | 'GET_SPEC_PACKAGE'
  | 'GET_SPEC_PACKAGES'
  | 'GET_PACKAGE_REVISIONS'
  | 'GET_LATEST_REVISION'
  | 'GET_REVISION_BY_ID'
  | 'GET_SPEC_PACKAGE_DOCUMENTS'
  | 'GET_SPEC_DOC'
  | 'LIST_PACKAGES'
  | 'LIST_COLLECTIONS'
  | 'GET_COLLECTION_BY_ID'
  | 'GET_COLLECTION_LINKS'
  | 'LIST_COLLECTION_REVISIONS'
  | 'GET_COLLECTION_REVISION_BY_ID'
  | 'GET_COLLECTION_REVISION_SOURCES'
  | 'GET_COLLECTION_ELEMENT_REVISIONS'
  | 'GET_COLLECTION_REVISION_DOCUMENTS'
  | 'GET_COLLECTION_REVISION_DOCUMENT_CONTENT'
  | 'SEMANTIC_SEARCH'
  // Knowledge Base (Bedrock RAG)
  | 'RETRIEVE_AND_GENERATE'
  | 'RETRIEVE'
  | 'LIST_KNOWLEDGE_BASES'
  | 'START_INGESTION'
  | 'UPLOAD_DOCUMENT';

/** Actor classification. */
export type ActorType = 'USER' | 'SERVICE' | 'SYSTEM';

/**
 * Per-capability backend resource configuration (all optional). Omitted fields
 * fall back to the capability default on the backend.
 */
export interface ExecutionResourceTypeBundle {
  metaInfoStoreType?: string;
  metaDataStoreType?: string;
  peopleResourceType?: string;
  knowledgeResourceType?: string;
  inferenceResourceType?: string;
  storeBundleInfoIdentifier?: string;
}

/** TS mirror of the Smithy MetaInfo structure (all wire fields required). */
export interface MetaInfo {
  jobName: string;
  executionIdentifier: string;
  actorIdentifier: string;
  actorType: ActorType;
  actorClient: string;
  jobCategory: JobCategory;
  requestType: RequestType;
  requestTimeStamp: string;
  requestDescription: string;
  executionResourceTypeBundle?: ExecutionResourceTypeBundle;
}

/** The client identifier stamped into actorClient for every request. */
export const ACTOR_CLIENT = 'EngineeringVectorUI';

export interface BuildMetaInfoArgs {
  identity: Identity;
  jobName: string;
  jobCategory: JobCategory;
  requestType: RequestType;
  description?: string;
  /** Defaults to a fresh uuid v4 if not supplied. */
  executionIdentifier?: string;
  executionResourceTypeBundle?: ExecutionResourceTypeBundle;
}

/**
 * Builds a fully-populated MetaInfo from the current identity plus per-request
 * classification. Fills actor attribution, timestamp, and execution id.
 */
export function buildMetaInfo(args: BuildMetaInfoArgs): MetaInfo {
  const {
    identity,
    jobName,
    jobCategory,
    requestType,
    description,
    executionIdentifier,
    executionResourceTypeBundle,
  } = args;

  // Multi-tenant stamping: read the active team's tenant bundle key (pure,
  // synchronous) and stamp it into storeBundleInfoIdentifier so EVERY request is
  // tenant-scoped. An empty bundle key (the built-in "current workspace") leaves
  // the identifier UNSET so requests resolve to the backend DEFAULT bundle —
  // preserving the exact pre-multi-tenant behavior. An onboarded team isolates
  // its data under bundleKey = bindleId. An explicitly-passed identifier (none
  // today) is never overridden.
  const bundleKey = getActiveTeamBundleKey();
  const resolvedBundle: ExecutionResourceTypeBundle | undefined = bundleKey
    ? {
        ...(executionResourceTypeBundle ?? {}),
        storeBundleInfoIdentifier:
          executionResourceTypeBundle?.storeBundleInfoIdentifier ?? bundleKey,
      }
    : executionResourceTypeBundle;

  return {
    jobName,
    executionIdentifier: executionIdentifier ?? uuidv4(),
    actorIdentifier: identity.alias,
    actorType: 'USER',
    actorClient: ACTOR_CLIENT,
    jobCategory,
    requestType,
    requestTimeStamp: new Date().toISOString(),
    requestDescription: description ?? `${requestType} via ${ACTOR_CLIENT}`,
    ...(resolvedBundle ? { executionResourceTypeBundle: resolvedBundle } : {}),
  };
}
